import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Code, Breadcrumbs, Divider, List } from '@mantine/core';
import { Lightbulb, Warning, Check, ArrowSquareOut, NumberOne, NumberTwo, NumberThree, NumberFour, NumberFive, NumberSix } from '@phosphor-icons/react';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import SEO from '../../components/SEO';

function StepCard({ number, title, children }) {
  return (
    <Paper withBorder p="lg" radius="md" mb="md">
      <Group gap="sm" mb="md" wrap="nowrap">
        <ThemeIcon size="lg" radius="xl" color="brand" variant="filled">
          <Text size="sm" fw={700}>{number}</Text>
        </ThemeIcon>
        <Title order={4}>{title}</Title>
      </Group>
      {children}
    </Paper>
  );
}

export default function GoogleAdsSetupGuide() {
  const guidesBase = useGuidesBase();

  return (
    <Box px="lg" py="md">
      <SEO
        title="Konfiguracja Google Ads - krok po kroku"
        description="Jak założyć konto Google Ads, skonfigurować płatności i połączyć je z Droid. Kompletny przewodnik konfiguracji od zera."
        path={`${guidesBase}/google-ads-setup`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Konfiguracja Google Ads</Text>
      </Breadcrumbs>
      <Title order={3} mb="xs">Konfiguracja konta Google Ads i połączenie z Droid</Title>
      <Text size="sm" c="dimmed" mb="lg">Kompletny przewodnik od zera do pierwszej kampanii. Czas: około 15-20 minut.</Text>

      <Alert color="blue" mb="lg" icon={<Lightbulb size={16} />}>
        <Text size="sm">
          Jeśli masz już konto Google Ads z dodanymi płatnościami, przejdź od razu do <Anchor href="#krok-5" size="sm">kroku 5 (połączenie z Droid)</Anchor>.
        </Text>
      </Alert>

      {/* Step 1 */}
      <StepCard number="1" title="Utwórz konto Google (jeśli nie masz)">
        <Text size="sm" lh={1.7} mb="sm">
          Konto Google (Gmail) jest wymagane do korzystania z Google Ads. Jeśli masz już konto Gmail, Dysk Google lub YouTube, to jest to samo konto. Przejdź do kroku 2.
        </Text>

        <Text size="sm" fw={500} mb="xs">Jak utworzyć konto:</Text>
        <List size="sm" spacing="xs" mb="md">
          <List.Item>Wejdź na <Anchor href="https://accounts.google.com/signup" target="_blank" size="sm">accounts.google.com/signup <ArrowSquareOut size={10} className="inline" /></Anchor></List.Item>
          <List.Item>Wypełnij formularz: imię, nazwisko, nazwa użytkownika (będzie to Twój adres email @gmail.com)</List.Item>
          <List.Item>Ustaw silne hasło (minimum 8 znaków, cyfry i litery)</List.Item>
          <List.Item>Dodaj numer telefonu do weryfikacji (Google wyśle kod SMS)</List.Item>
          <List.Item>Zaakceptuj regulamin i kliknij "Utwórz konto"</List.Item>
        </List>

        <Alert color="yellow" icon={<Warning size={14} />}>
          <Text size="xs">
            <strong>Dla firm:</strong> Zalecamy użycie firmowego adresu email (np. jan@twoja-firma.pl) zamiast prywatnego Gmail.
            Możesz utworzyć konto Google z dowolnym adresem email na stronie <Anchor href="https://accounts.google.com/signup?hl=pl" target="_blank" size="xs">rejestracji</Anchor>, wybierając opcję "Użyj istniejącego adresu email".
          </Text>
        </Alert>
      </StepCard>

      {/* Step 2 */}
      <StepCard number="2" title="Utwórz konto Google Ads">
        <Text size="sm" lh={1.7} mb="sm">
          Konto Google Ads to osobny panel do zarządzania reklamami. Jest bezpłatne, płacisz tylko za kliknięcia w reklamy.
        </Text>

        <Text size="sm" fw={500} mb="xs">Instrukcja krok po kroku:</Text>
        <List size="sm" spacing="sm" mb="md" type="ordered">
          <List.Item>
            Wejdź na <Anchor href="https://ads.google.com" target="_blank" size="sm">ads.google.com <ArrowSquareOut size={10} className="inline" /></Anchor> i kliknij niebieski przycisk <strong>"Zacznij teraz"</strong>
          </List.Item>
          <List.Item>
            Zaloguj się swoim kontem Google (z kroku 1)
          </List.Item>
          <List.Item>
            <Text size="sm" lh={1.7}>
              <strong>Ważne:</strong> Google od razu spróbuje przeprowadzić Cię przez kreator kampanii. Nie twórz kampanii przez ten kreator. Zamiast tego:
            </Text>
            <Paper withBorder p="sm" radius="sm" mt="xs" mb="xs" bg="gray.0">
              <Stack gap={4}>
                <Text size="xs">1. Na pierwszym ekranie kreatora kliknij mały link na dole: <strong>"Przejdź do trybu eksperta"</strong></Text>
                <Text size="xs">2. Na następnym ekranie wybierz <strong>"Utwórz konto bez kampanii"</strong> (link na samym dole)</Text>
                <Text size="xs">3. To da Ci pusty panel Google Ads bez żadnych kampanii</Text>
              </Stack>
            </Paper>
            <Text size="xs" c="dimmed">Dlaczego? Kreator Google tworzy kampanie z domyślnymi ustawieniami, które są nieoptymalne. Droid utworzy znacznie lepszą kampanię na podstawie analizy Twojej strony.</Text>
          </List.Item>
          <List.Item>
            <Text size="sm" lh={1.7}>
              Potwierdź ustawienia konta:
            </Text>
            <Paper withBorder p="sm" radius="sm" mt="xs">
              <Stack gap={6}>
                <Group gap="xs"><Badge size="xs" color="brand">Waluta</Badge><Text size="sm">PLN (polski złoty)</Text></Group>
                <Group gap="xs"><Badge size="xs" color="brand">Strefa czasowa</Badge><Text size="sm">(UTC+01:00) Europa/Warszawa</Text></Group>
                <Group gap="xs"><Badge size="xs" color="brand">Kraj</Badge><Text size="sm">Polska</Text></Group>
              </Stack>
            </Paper>
          </List.Item>
          <List.Item>
            Kliknij <strong>"Prześlij"</strong>. Twoje konto Google Ads jest gotowe.
          </List.Item>
        </List>

        <Alert color="red" icon={<Warning size={14} />}>
          <Text size="xs">
            <strong>Uwaga:</strong> Walutę i strefę czasową <strong>nie można zmienić</strong> po utworzeniu konta. Upewnij się, że wybrałeś PLN i Europę/Warszawę. Jeśli pomyliłeś walutę, będziesz musiał utworzyć nowe konto.
          </Text>
        </Alert>
      </StepCard>

      {/* Step 3 */}
      <StepCard number="3" title="Dodaj dane rozliczeniowe">
        <Text size="sm" lh={1.7} mb="sm">
          Google Ads wymaga metody płatności, żeby wyświetlać reklamy. Bez niej konto jest aktywne, ale reklamy nie będą działać.
        </Text>

        <Text size="sm" fw={500} mb="xs">Gdzie znaleźć ustawienia płatności:</Text>
        <Paper withBorder p="sm" radius="sm" mb="md" bg="gray.0">
          <Text size="xs">
            Panel Google Ads → ikona klucza (Narzędzia) w górnym menu → sekcja <strong>"Rozliczenia"</strong> → <strong>"Ustawienia płatności"</strong>
          </Text>
        </Paper>

        <Text size="sm" fw={500} mb="xs">Dane do wypełnienia:</Text>
        <List size="sm" spacing="sm" mb="md">
          <List.Item>
            <strong>Typ konta:</strong> "Firmowe" (jeśli masz NIP) lub "Indywidualne"
          </List.Item>
          <List.Item>
            <strong>Dane firmy:</strong> Nazwa firmy, adres, NIP (dla faktury VAT)
          </List.Item>
          <List.Item>
            <strong>Metoda płatności:</strong>
            <Stack gap={4} mt="xs">
              <Group gap="xs"><ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm"><strong>Karta kredytowa/debetowa</strong> - zalecane, natychmiastowa aktywacja</Text></Group>
              <Group gap="xs"><ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm"><strong>Przelew bankowy</strong> - dłuższy czas aktywacji (1-3 dni robocze)</Text></Group>
            </Stack>
          </List.Item>
        </List>

        <Alert color="blue" icon={<Lightbulb size={14} />}>
          <Text size="xs">
            <strong>Tip:</strong> Google pobiera opłaty automatycznie po osiągnięciu progu (zwykle 200 zł) lub co 30 dni.
            Nie musisz doładowywać konta z góry. Ustawiając budżet dzienny np. 30 zł, wydasz maksymalnie ~900 zł/mc.
          </Text>
        </Alert>

        <Divider my="md" />

        <Text size="sm" fw={500} mb="xs">Faktury VAT:</Text>
        <Text size="sm" c="dimmed" lh={1.7}>
          Google automatycznie wystawia faktury VAT co miesiąc. Znajdziesz je w: Narzędzia → Rozliczenia → Dokumenty.
          Jeśli podałeś NIP przy rejestracji, faktury będą z odliczeniem VAT (23% netto + VAT dla polskich firm).
        </Text>
      </StepCard>

      {/* Step 4 */}
      <StepCard number="4" title="Skonfiguruj śledzenie konwersji (zalecane)">
        <Text size="sm" lh={1.7} mb="sm">
          Śledzenie konwersji to sposób, w jaki Google Ads (i Droid) wie, czy kliknięcie w reklamę doprowadziło do pożądanej akcji: zakupu, wypełnienia formularza, lub połączenia telefonicznego.
        </Text>

        <Text size="sm" fw={500} mb="xs">Dlaczego to ważne:</Text>
        <List size="sm" spacing="xs" mb="md">
          <List.Item><strong>Bez konwersji:</strong> Droid optymalizuje pod kliknięcia (CTR, CPC) - to lepsze niż nic, ale nie gwarantuje sprzedaży</List.Item>
          <List.Item><strong>Z konwersjami:</strong> Droid optymalizuje pod sprzedaż (ROAS, CPA) - skupia budżet na słowach, które generują przychód</List.Item>
          <List.Item>Smart Bidding (Maximize Conversions, Target CPA) wymaga danych o konwersjach</List.Item>
        </List>

        <Paper withBorder p="sm" radius="md" bg="gray.0" mb="md">
          <Group gap="sm">
            <ThemeIcon variant="light" color="brand" size="md" radius="md">
              <Lightbulb size={16} />
            </ThemeIcon>
            <Box>
              <Text size="sm" fw={500}>Szczegółowy przewodnik</Text>
              <Anchor component={Link} to={`${guidesBase}/conversions`} size="sm">
                Otwórz przewodnik śledzenia konwersji →
              </Anchor>
            </Box>
          </Group>
        </Paper>

        <Text size="sm" c="dimmed" lh={1.7}>
          Możesz pominąć ten krok i wrócić do niego później. Droid będzie działać bez konwersji, ale z nimi jest znacznie skuteczniejszy.
          Większość platform e-commerce (WooCommerce, Shopify, PrestaShop) ma wtyczki, które konfigurują śledzenie automatycznie.
        </Text>
      </StepCard>

      {/* Step 5 */}
      <div id="krok-5">
        <StepCard number="5" title="Połącz konto Google Ads z Droid">
          <Text size="sm" lh={1.7} mb="sm">
            Teraz połączymy Twoje konto Google Ads z Droid. Droid potrzebuje uprawnień do odczytu danych kampanii i zapisu zmian (stawki, budżety, słowa kluczowe, reklamy).
          </Text>

          <Text size="sm" fw={500} mb="xs">Instrukcja:</Text>
          <List size="sm" spacing="sm" mb="md" type="ordered">
            <List.Item>
              Zaloguj się do <Anchor component={Link} to="/app" size="sm">Droid (droid.pl/app)</Anchor>
            </List.Item>
            <List.Item>
              Przejdź do zakładki <strong>Kampanie</strong> w lewym menu
            </List.Item>
            <List.Item>
              Kliknij przycisk <strong>"Połącz Google Ads"</strong> (widoczny jeśli nie masz połączonego konta)
            </List.Item>
            <List.Item>
              <Text size="sm" lh={1.7}>
                Otworzy się okno logowania Google. Zaloguj się <strong>tym samym kontem</strong>, na którym masz Google Ads.
              </Text>
              <Alert color="yellow" icon={<Warning size={14} />} mt="xs">
                <Text size="xs">
                  Jeśli widzisz błąd "Dostęp zablokowany": wyczyść cookies przeglądarki, lub otwórz Droid w trybie incognito i zaloguj się ponownie.
                  Problem wynika z wielu sesji Google w jednej przeglądarce.
                </Text>
              </Alert>
            </List.Item>
            <List.Item>
              Zatwierdź uprawnienia klikając <strong>"Zezwól"</strong> na ekranie zgody Google.
              Droid prosi o dostęp do zarządzania kampaniami Google Ads.
            </List.Item>
            <List.Item>
              <Text size="sm" lh={1.7}>
                Jeśli masz więcej niż jedno konto Google Ads (np. konto MCC z wieloma klientami), wybierz odpowiednie konto z listy.
              </Text>
            </List.Item>
            <List.Item>
              <Text size="sm" lh={1.7}>
                Po połączeniu Droid automatycznie zsynchronizuje Twoje kampanie. Zobaczysz je na liście w ciągu 1-2 minut.
              </Text>
            </List.Item>
          </List>

          <Alert color="green" icon={<Check size={14} />}>
            <Text size="xs">
              <strong>Gotowe!</strong> Po pomyślnym połączeniu zobaczysz swoje kampanie na liście. Ikona synchronizacji w prawym górnym rogu pozwala ręcznie odświeżyć dane.
            </Text>
          </Alert>
        </StepCard>
      </div>

      {/* Step 6 */}
      <StepCard number="6" title="Utwórz pierwszą kampanię z agentem AI">
        <Text size="sm" lh={1.7} mb="sm">
          Jeśli masz już kampanie w Google Ads, Droid zsynchronizował je automatycznie. Możesz włączyć zarządzanie Droid dla wybranych kampanii przyciskiem toggle.
        </Text>

        <Text size="sm" lh={1.7} mb="sm">
          Jeśli zaczynasz od zera, Droid utworzy kampanię za Ciebie:
        </Text>

        <List size="sm" spacing="sm" mb="md" type="ordered">
          <List.Item>Kliknij <strong>"Utwórz kampanię"</strong> na stronie Kampanie</List.Item>
          <List.Item>Wpisz adres swojej strony internetowej (np. <Code>twoja-firma.pl</Code>)</List.Item>
          <List.Item>Wybierz zasięg: lokalny (miasto), ogólnopolski, lub międzynarodowy</List.Item>
          <List.Item>
            <Text size="sm" lh={1.7}>
              Agent AI przeanalizuje Twoją stronę i przygotuje:
            </Text>
            <Stack gap={4} mt="xs">
              <Group gap="xs"><ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm">Słowa kluczowe z wolumenami wyszukiwań i szacowanymi CPC</Text></Group>
              <Group gap="xs"><ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm">Treści reklam (nagłówki i opisy dopasowane do Twojej oferty)</Text></Group>
              <Group gap="xs"><ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm">Sugerowany budżet dzienny na podstawie analizy rynku</Text></Group>
              <Group gap="xs"><ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm">Analizę konkurencji (kto reklamuje się na te same frazy)</Text></Group>
              <Group gap="xs"><ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={8} /></ThemeIcon><Text size="sm">Rekomendację strategii stawek</Text></Group>
            </Stack>
          </List.Item>
          <List.Item>Przejrzyj i edytuj sugestie agenta (możesz zmienić słowa kluczowe, budżet, treści reklam)</List.Item>
          <List.Item>Wybierz plan: trial 7 dni za darmo lub subskrypcja 299 zł/mc</List.Item>
          <List.Item>
            <Text size="sm" lh={1.7}>
              Kliknij <strong>"Opublikuj"</strong>. Kampania zostanie utworzona w Google Ads jako <strong>wstrzymana</strong>.
              Gdy będziesz gotowy, włącz ją przyciskiem "Wznów kampanię" w szczegółach kampanii.
            </Text>
          </List.Item>
        </List>

        <Text size="sm" c="dimmed" lh={1.7}>
          Po uruchomieniu kampanii Droid zacznie ją monitorować i optymalizować 24/7: dostosowywać stawki, dodawać wykluczenia, testować reklamy i wysyłać raporty.
        </Text>
      </StepCard>

      {/* Common issues */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Title order={4} mb="md">Częste pytania i problemy</Title>
        <Stack gap="lg">
          <Box>
            <Text size="sm" fw={600} mb={4}>Nie widzę swojego konta Google Ads po połączeniu</Text>
            <Text size="sm" c="dimmed" lh={1.7}>
              Upewnij się, że logujesz się tym samym kontem Google, na którym jest Google Ads.
              Jeśli masz konto MCC (Manager), musisz wybrać konkretne konto klienta z listy.
              Sprawdź w Google Ads: Narzędzia → Ustawienia → Informacje o koncie → ID klienta. To 10-cyfrowy numer.
            </Text>
          </Box>
          <Divider />
          <Box>
            <Text size="sm" fw={600} mb={4}>Błąd "Dostęp zablokowany" lub "invalid_user" przy łączeniu</Text>
            <Text size="sm" c="dimmed" lh={1.7}>
              Ten błąd pojawia się gdy przeglądarka ma zapisane dane logowania z wielu kont Google.
              Rozwiązanie: otwórz Droid w trybie incognito (Ctrl+Shift+N w Chrome), zaloguj się do Droid, a potem kliknij "Połącz Google Ads".
              Alternatywnie: wyczyść cookies dla accounts.google.com i spróbuj ponownie.
            </Text>
          </Box>
          <Divider />
          <Box>
            <Text size="sm" fw={600} mb={4}>Kampanie nie synchronizują się</Text>
            <Text size="sm" c="dimmed" lh={1.7}>
              Pierwsza synchronizacja może potrwać do 2 minut. Kliknij ikonę synchronizacji (dwie strzałki) na stronie kampanii.
              Jeśli po 5 minutach wciąż nie ma kampanii, sprawdź w Ustawieniach czy token Google Ads nie wygasł.
              Nowe konta Google Ads (bez kampanii) pokażą pustą listę, co jest prawidłowe.
            </Text>
          </Box>
          <Divider />
          <Box>
            <Text size="sm" fw={600} mb={4}>Jaki minimalny budżet potrzebuję?</Text>
            <Text size="sm" c="dimmed" lh={1.7}>
              Google Ads nie ma oficjalnego minimum, ale zalecamy:
            </Text>
            <Stack gap={4} mt="xs">
              <Group gap="xs"><Text size="sm" fw={500} w={160}>Usługi lokalne:</Text><Text size="sm">20-30 zł/dzień</Text></Group>
              <Group gap="xs"><Text size="sm" fw={500} w={160}>Sklep internetowy:</Text><Text size="sm">30-50 zł/dzień</Text></Group>
              <Group gap="xs"><Text size="sm" fw={500} w={160}>B2B / SaaS:</Text><Text size="sm">50-100 zł/dzień</Text></Group>
            </Stack>
            <Text size="sm" c="dimmed" mt="xs">Przy budżecie poniżej 20 zł/dzień algorytm Google nie ma wystarczająco danych do optymalizacji.</Text>
          </Box>
          <Divider />
          <Box>
            <Text size="sm" fw={600} mb={4}>Czy Droid może zarządzać istniejącymi kampaniami?</Text>
            <Text size="sm" c="dimmed" lh={1.7}>
              Tak. Po połączeniu konta Droid zsynchronizuje wszystkie istniejące kampanie.
              Dla każdej kampanii możesz włączyć zarządzanie Droid osobnym przyciskiem toggle.
              Droid nie zmieni niczego w kampaniach, których nie zarządza.
            </Text>
          </Box>
        </Stack>
      </Paper>

      {/* Checklist */}
      <Paper withBorder p="lg" radius="md">
        <Title order={4} mb="md">Checklista gotowości</Title>
        <Text size="sm" c="dimmed" mb="md">Przed uruchomieniem pierwszej kampanii upewnij się, że:</Text>
        <Stack gap="xs">
          {[
            'Konto Google Ads utworzone z walutą PLN i strefą Europa/Warszawa',
            'Metoda płatności dodana i zweryfikowana (karta lub przelew)',
            'Dane firmy uzupełnione (NIP dla faktur VAT)',
            'Konto połączone z Droid (widoczne w Ustawieniach → Google Ads)',
            'Kampanie zsynchronizowane (widoczne na liście kampanii)',
            'Pierwsza kampania utworzona i przejrzana',
            'Śledzenie konwersji skonfigurowane (opcjonalnie, ale zalecane)',
            'Budżet dzienny ustawiony na realistycznym poziomie (min. 20-30 zł)',
          ].map((item, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm">{item}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
